import React, {useEffect, useRef, useState, useCallback} from 'react'
import {useLocation} from 'react-router-dom'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Text,
    Image,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    useDisclosure,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getUrl} from '../../utils/cloudinary-utils'
import EngravingAPI from './engravingAPI'
import cryptojs from 'crypto-js'
import {useAddToCartDrawerContext} from '../../hooks/use-add-to-cart-drawer'
import useMonetate from '../../hooks/use-monetate'
import useTagManager from '../../hooks/use-tag-manager'
import {useSiteContext} from '../../contexts/site'
import LoadingSpinner from '../loading-spinner'
import EngravingSkeleton from './partials/engraving-skeleton'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import EngravingOptions from './partials/engraving-options'
import FontOptions from './partials/font-options'
import LineOne from './partials/lines/line-one'
import LineThree from './partials/lines/line-three'
import LineFour from './partials/lines/line-four'
import LineFive from './partials/lines/line-five'
import LineTwo from './partials/lines/line-two'
import EngravingInstructions from './partials/engraving-instructions'
import EngravingButtons from './partials/engraving-buttons'
import EngravingPurchasePath from './partials/engraving-purchase-path'
import {
    isRedtrictedWordActive,
    makeEngravingGtmObject,
    getZodiacSigns
} from '../../utils/engraving-utils'

/**
 * Engravable component allows adding personalization engraving to a product.
 */
const Engravable = ({
    product,
    setDriveRecipeID,
    disableAddToBag = false,
    setEngravingChecksum,
    addToCart,
    variant,
    customer,
    isEditEngraving,
    setEngravingGtmObject,
    setDesktopGalleryEngImages,
    basket,
    priceData,
    setOpenEngraving,
    openEngraving,
    driveRecipeID,
    wishlist
}) => {
    const {
        app: {commerceAPI}
    } = getConfig()
    const location = useLocation()
    const navigate = useNavigation()
    const monetate = useMonetate()
    const tagManager = useTagManager()
    const {setOpenBonusModal} = useSiteContext()
    var clientRoot,
        blueprintUrl,
        apiKey,
        uploadUrl,
        recipeUrl,
        highlightTitle,
        highlightDescription,
        restrictionListArray = []
    const {engraving} = commerceAPI
    const {config} = engraving || {}
    const fontsMapping = {
        'clarendon.ttf': 'Clarendon',
        'futur.otf': 'Futura',
        'script.ttf': 'Vanessa',
        'x-monogram-init.otf,x-monogram-medi.otf,x-monogram-fina.otf,x-monogram-medi.otf':
            'Monogram'
    }
    try {
        if (config) {
            const jsonConfig = JSON.parse(config)
            clientRoot = jsonConfig.clientRoot
            blueprintUrl = jsonConfig.blueprintUrl
            apiKey = jsonConfig.apiKey
            ;(highlightTitle = jsonConfig.highlightTitle),
                (highlightDescription = jsonConfig.highlightDescription),
                (restrictionListArray = jsonConfig?.restrictionList?.split(','))
            uploadUrl = jsonConfig.uploadUrl
            recipeUrl = jsonConfig.recipeUrl
        }
    } catch (e) {
        console.log(e.message)
    }

    const isDesktop = useBreakpointValue({base: false, lg: true})
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {onOpen: onAddToCartDrawerOpen, onClose: onAddToCartDrawerClose} =
        useAddToCartDrawerContext()
    const containerRef = useRef(null)
    const bodyRef = useRef(null)
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const [showAddedCta, setShowAddedCta] = useState(false)
    const [productUuid, setProductUuid] = useState('')
    const [productQuantity, setProductQuantity] = useState('')
    const [showInitialSpinner, setShowInitialSpinner] = useState(true)
    const [showApiSpinner, setShowApiSpinner] = useState(false)
    const [selectedHeading, setSelectedHeading] = useState('type')
    const [displayIconType, setDisplayIconType] = useState('')
    const [selectedFace, setSelectedFace] = useState('face_1')
    const [shouldShowLineToggle, setShouldShowLineToggle] = useState(false)
    const [shouldShow3LineToggle, setShouldShow3LineToggle] = useState(false)
    const [shouldShow4LineToggle, setShouldShow4LineToggle] = useState(false)
    const [shouldShow5LineToggle, setShouldShow5LineToggle] = useState(false)
    const [visibleLine, setVisibleLine] = useState('line1')
    const [showFonts, setShowFonts] = useState(true)
    const [maxLength, setMaxLength] = useState(0)
    const [selectedFaceCode, setSelectedFaceCode] = useState('')
    const [selectedFont, setSelectedFont] = useState({
        face_1: 'Clarendon',
        face_2: 'Clarendon'
    })
    const isMonogram = selectedFont[selectedFace] === 'Monogram'
    const [engravingImages, setEngravingImages] = useState({
        face_1: {line1: '', line2: '', line3: '', line4: '', line5: '', image: '', code: ''},
        face_2: {line1: '', line2: '', line3: '', line4: '', line5: '', image: '', code: ''}
    })
    const [engravingLines, setEngravingLines] = useState({
        face_1: {
            line1: '',
            line2: '',
            line3: '',
            line4: '',
            line5: '',
            monogram: ''
        },
        face_2: {
            line1: '',
            line2: '',
            line3: '',
            line4: '',
            line5: '',
            monogram: ''
        }
    })

    useEffect(() => {
        onAddToCartDrawerClose()
    }, [location?.pathname])

    useEffect(() => {
        if (product?.master?.masterId) {
            setEngravingImages({
                face1: null,
                face2: null
            })
            setDesktopGalleryEngImages(null)
        }
    }, [product?.master?.masterId])

    const fetchEngravingData = async (params, paramsReceipeId) => {
        const engraving = new EngravingAPI()
        const recipe = await engraving.fetchReceipeData(recipeUrl, paramsReceipeId)
        if (!recipe) {
            // If no recipe is returned, remove parameters from the URL.
            navigate(location.pathname)
        }
        setProductUuid(params.get('uuid') || '')
        setProductQuantity(params.get('Quantity') || 1)
        const engravingComponents = recipe.components.filter(
            (c) => c?.component?.custom?.['personalization-text']
        )

        const lines = {
            face_1: {line1: '', line2: '', line3: '', line4: '', line5: '', monogram: ''},
            face_2: {line1: '', line2: '', line3: '', line4: '', line5: '', monogram: ''}
        }
        let images = {face_1: {}, face_2: {}}
        const fonts = {face_1: 'Clarendon', face_2: 'Clarendon'}

        engravingComponents.forEach((c) => {
            const face = c.custom?.['ks-face']
            if (!face) return
            const text = c.component?.custom?.['personalization-text'] || ''
            const isMonogram =
                c.component?.custom?.['personalization-font']?.indexOf('monogram') !== -1
            const split = text.split('\n')
            lines[face] = {
                line1: split[0] && !isMonogram ? split[0] : '',
                line2: split[1] && !isMonogram ? split[1] : '',
                line3: split[2] && !isMonogram ? split[2] : '',
                line4: split[3] && !isMonogram ? split[3] : '',
                line5: split[4] && !isMonogram ? split[4] : '',
                monogram: isMonogram ? split[0] : '',
                code: c?.code || ''
            }
            const faceImg = recipe?.views?.find((v) => v.code === face)?.preview
            images[face] = {...lines[face], image: (faceImg ? 'https:' + faceImg : '') || ''}
            const fontName = c.component?.custom?.['personalization-font']
            if (fontName && fontsMapping[fontName]) fonts[face] = fontsMapping[fontName]
            const maxLen = parseInt(c.component?.custom?.['ks-max-length'] || '0', 10)
            if (maxLen) setMaxLength(maxLen)
        })

        setEngravingLines(lines)
        setEngravingImages(images)
        setDesktopGalleryEngImages(images)
        setSelectedFont(fonts)
        setSelectedFaceCode(recipe?.views?.[0]?.code || '')
        setShouldShowLineToggle(true)
        setShouldShow3LineToggle(true)
        setShouldShow4LineToggle(true)
        setShouldShow5LineToggle(true)
        setShowFonts(true)

        // engrave each face with its respective lines
        Object.keys(lines).forEach((faceId) => {
            const isBack = faceId === 'face_2'
            const placement = recipe.components.find((c) =>
                c.code?.endsWith(isBack ? '-BACKTEXT' : '-FRONTTEXT')
            )

            if (!placement) return
            window.engravingController?.updatePersonalization(
                placement.code,
                isMonogram
                    ? lines[faceId]?.['monogram']?.length === 3
                        ? lines[faceId]?.['monogram']
                        : ''
                    : `${lines[faceId].line1 || ''}${
                          lines[faceId].line2 ? '\n' + lines[faceId].line2 : ''
                      }${lines[faceId].line3 ? '\n' + lines[faceId].line3 : ''}${
                          lines[faceId].line4 ? '\n' + lines[faceId].line4 : ''
                      }${lines[faceId].line5 ? '\n' + lines[faceId].line5 : ''}`
            )
        })

        /** Set the states for Checksum and Drive Recipe ID **/
        setDriveRecipeID(paramsReceipeId)
        const isBackOnly = recipe?.views?.[0]?.name === 'Back'
        const face1LinesAvailable =
            fonts.face_1 === 'Monogram'
                ? lines.face_1.monogram
                : lines.face_1.line1 ||
                  lines.face_1.line2 ||
                  lines.face_1.line3 ||
                  lines.face_1.line4 ||
                  lines.face_1.line5
        const activeFace2 = isBackOnly ? 'face_1' : 'face_2'
        const face2LinesAvailable =
            fonts?.[activeFace2] === 'Monogram'
                ? lines[activeFace2].monogram
                : lines[activeFace2].line1 ||
                  lines[activeFace2].line2 ||
                  lines[activeFace2].line3 ||
                  lines[activeFace2].line4 ||
                  lines[activeFace2].line5
        const activeStates = {
            face_1: {
                name: 'face_1-text',
                hasValue: isBackOnly ? false : !!face1LinesAvailable,
                value: isBackOnly
                    ? ''
                    : fonts?.face_1 === 'Monogram'
                    ? lines?.face_1.monogram
                    : `${lines.face_1.line1 || ''}
                                                                    ${
                                                                        lines.face_1.line2
                                                                            ? '\n' +
                                                                              lines.face_1.line2
                                                                            : ''
                                                                    }
                                                                    ${
                                                                        lines.face_1.line3
                                                                            ? '\n' +
                                                                              lines.face_1.line3
                                                                            : ''
                                                                    }
                                                                    ${
                                                                        lines.face_1.line4
                                                                            ? '\n' +
                                                                              lines.face_1.line4
                                                                            : ''
                                                                    }
                                                                    ${
                                                                        lines.face_1.line5
                                                                            ? '\n' +
                                                                              lines.face_1.line5
                                                                            : ''
                                                                    }`.trim(),
                fontFamily: fonts.face_1
            },
            face_1_right: {
                name: 'face_1-right-text',
                hasValue: false,
                value: ''
            },
            face_2: {
                name: 'face_2-text',
                hasValue: !!face2LinesAvailable,
                value:
                    fonts?.[isBackOnly ? 'face_1' : 'face_2'] === 'Monogram'
                        ? lines?.[isBackOnly ? 'face_1' : 'face_2']?.monogram
                        : `${lines?.[isBackOnly ? 'face_1' : 'face_2'].line1}\n${
                              lines?.[isBackOnly ? 'face_1' : 'face_2'].line2
                          }\n${lines?.[isBackOnly ? 'face_1' : 'face_2'].line3}\n${
                              lines?.[isBackOnly ? 'face_1' : 'face_2'].line4
                          }\n${lines?.[isBackOnly ? 'face_1' : 'face_2'].line5}`.trim(),
                fontFamily: fonts?.[isBackOnly ? 'face_1' : 'face_2']
            },
            face_2_right: {
                name: 'face_2-right-text',
                hasValue: false,
                value: '',
                fontFamily: ''
            }
        }

        const engravingChecksum = getHash(activeStates)
        setEngravingChecksum(engravingChecksum)

        /** Refresh the gallery images and engraving choice in the purchase path **/
        const img1 = images.face_1?.image || null

        const img2 = isBackOnly ? images.face_1?.image : images.face_2?.image
        const face1Name = recipe.views?.find((v) => v.code === 'face_1')?.name || ''
        const face2Name = recipe.views?.find((v) => v.code === 'face_2')?.name || ''
        images = {
            face1: {
                image: face1LinesAvailable || isBackOnly ? img1 : null,
                name: face1LinesAvailable || isBackOnly ? face1Name : null
            },
            face2: {
                image: face2LinesAvailable && !isBackOnly ? img2 : null,
                name: face2LinesAvailable && !isBackOnly ? face2Name : null
            }
        }

        setEngravingImages(images)
        setDesktopGalleryEngImages(images)
    }
    /**
     * Fetch engraving data by using ReceipeID
     */
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const paramsReceipeId = params.get('driveRecipeID')
        if (!paramsReceipeId) return
        fetchEngravingData(params, paramsReceipeId)
    }, [location.search])

    // When face code and selected font is changed.
    useEffect(() => {
        if (selectedFaceCode && window?.engravingInstance?.configuratorSetup) {
            if (window?.engravingInstance?.configuratorSetup?.views) {
                let facesAvailable = 0
                Object.values(window.engravingInstance.configuratorSetup.views).forEach((view) => {
                    const btn = document.querySelector(`#${view.code}`)
                    if (!btn) return
                    if (
                        view?.code === 'face_2' &&
                        window?.engravingInstance?.configuratorSetup?.views?.face_1?.name === 'Back'
                    ) {
                        return
                    }
                    if (window?.engravingInstance?.configuratorSetup?.views?.face_1 === 'Back') {
                        view = window?.engravingInstance?.configuratorSetup?.views?.face_1
                    }
                    btn.textContent = view.name
                    btn.classList.remove('hide-element')
                    Object.entries(view).forEach(([key, value]) => {
                        if (key !== 'placements') {
                            btn.dataset[key] = String(value)
                        }
                    })
                    const isMonogramProduct = selectedFont[view?.code] === 'Monogram'
                    let nonProductPlacement = view.placements.find(
                        (p) =>
                            p.code !== 'Product' &&
                            (isMonogramProduct
                                ? p.code?.indexOf('MONOGRAM') !== -1
                                : p.code?.indexOf('MONOGRAM') === -1)
                    )

                    if (nonProductPlacement) {
                        btn.dataset.code = nonProductPlacement.code
                    }
                    facesAvailable += 1
                })
                if (bodyRef?.current) {
                    if (facesAvailable > 1) {
                        document
                            .querySelector('.engraving-drawer-body')
                            .classList.remove('single-face')
                    } else {
                        document
                            .querySelector('.engraving-drawer-body')
                            .classList.add('single-face')
                    }
                }
            }
        }

        if (selectedFont && window?.engravingInstance?.configuratorSetup?.options) {
            Object.values(window?.engravingInstance.configuratorSetup.options).forEach((opt) => {
                if (opt.definition === 'Engraving Font') {
                    document.querySelectorAll('.engraving-fonts div').forEach((fontBox) => {
                        const label = fontBox.getAttribute('data-name')
                        if (label.toLowerCase() === opt.name.toLowerCase()) {
                            fontBox.classList.remove('hide-element')
                            fontBox.dataset.font = opt.custom['personalization-font'] || ''
                            fontBox.dataset.code = opt.code
                            fontBox.dataset.name = opt.name
                        }
                    })
                }
            })
        }
    }, [selectedFaceCode, selectedFont, isOpen, bodyRef?.current])
    /**
     * Handle face selection (e.g., front/back).
     */
    const handleFaceSelect = useCallback(
        async (e) => {
            setShowApiSpinner(true)
            const isBack =
                window?.engravingInstance?.configuratorSetup?.views?.face_1?.name === 'Back'
            const faceId = isBack ? 'face_1' : e.target.id
            const frontPlacement = isBack
                ? Object.values(window?.engravingInstance?.configuratorSetup.groupPlacements).find(
                      (k) =>
                          k.custom?.['ks-face'] === 'face_1' &&
                          (selectedFont[faceId] === 'Monogram'
                              ? k.code?.indexOf('MONOGRAM') !== -1
                              : k.code?.indexOf('MONOGRAM') === -1)
                  )
                : {}
            const code = isBack
                ? frontPlacement?.code
                : engravingLines[faceId]?.['code'] || e.target.getAttribute('data-code')

            setSelectedFace(faceId)
            setSelectedFaceCode(code)

            const siblingCode = getSiblingId(
                code,
                window?.engravingInstance?.configuratorSetup.groupPlacements
            )
            if (siblingCode) {
                await window.engravingController?.updatePersonalization(siblingCode, '') // Safe to remove engraving from other fonts. We're doing this just because of the monogram behavior.
            }
            await window.engravingImage?.selectViewByCode(faceId)
            await window.engravingController?.updatePersonalization(
                code,
                selectedFont[faceId] === 'Monogram'
                    ? engravingLines[faceId]?.['monogram']?.length === 3
                        ? engravingLines[faceId]?.['monogram']
                        : ''
                    : `${engravingLines[faceId].line1}${
                          engravingLines[faceId].line2 ? '\n' + engravingLines[faceId].line2 : ''
                      }${engravingLines[faceId].line3 ? '\n' + engravingLines[faceId].line3 : ''}${
                          engravingLines[faceId].line4 ? '\n' + engravingLines[faceId].line4 : ''
                      }${engravingLines[faceId].line5 ? '\n' + engravingLines[faceId].line5 : ''}`
            )

            const linesAvailable =
                window.engravingInstance?.configuratorSetup?.groupPlacements?.[code]?.lines
            setShouldShowLineToggle(linesAvailable > 1)
            setShouldShow3LineToggle(linesAvailable > 2)
            setShouldShow4LineToggle(linesAvailable > 3)
            setShouldShow5LineToggle(linesAvailable > 4)
            setEngravingLines((prev) => {
                const updated = {
                    ...prev,
                    [selectedFace]: {
                        ...prev[selectedFace],
                        ['code']: selectedFaceCode
                    }
                }
                return updated
            })
            window.setTimeout(() => {
                setShowApiSpinner(false)
            }, 1500)
        },
        [engravingLines]
    )

    /**
     * Handle engraving text change.
     */
    const handleLineChange = (value, lineKey) => {
        setEngravingLines((prev) => {
            const updated = {
                ...prev,
                [selectedFace]: {
                    ...prev[selectedFace],
                    [selectedFont[selectedFace] === 'Monogram' ? 'monogram' : lineKey]: value,
                    ['code']: selectedFaceCode
                }
            }
            if (selectedFaceCode) {
                window.engravingController?.updatePersonalization(
                    selectedFaceCode,
                    selectedFont[selectedFace] === 'Monogram'
                        ? updated[selectedFace]?.['monogram']?.length === 3
                            ? updated[selectedFace]?.['monogram']
                            : ''
                        : `${updated[selectedFace].line1}${
                              updated[selectedFace].line2 ? '\n' + updated[selectedFace].line2 : ''
                          }${
                              updated[selectedFace].line3 ? '\n' + updated[selectedFace].line3 : ''
                          }${
                              updated[selectedFace].line4 ? '\n' + updated[selectedFace].line4 : ''
                          }${updated[selectedFace].line5 ? '\n' + updated[selectedFace].line5 : ''}`
                )
            }
            return updated
        })
    }

    const saveRecipe = () => {
        return window.engravingInstance.controller.saveRecipe({})
    }

    const getHash = (obj) => {
        var statesString = JSON.stringify(obj)
        var hash = cryptojs.HmacSHA256(statesString, 'KEY')

        return cryptojs.enc.Base64.stringify(hash)
    }

    function getSiblingId(code, groupPlacements) {
        const keys = Object.keys(groupPlacements)
        if (code?.includes('FRONT')) {
            return keys?.find((k) => k.includes('FRONT') && k !== code)
        }
        if (code?.includes('BACK')) {
            return keys?.find((k) => k.includes('BACK') && k !== code)
        }
        return null
    }

    /**
     * Handle font change.
     */
    const handleFontChange = async (e) => {
        setShowApiSpinner(true)
        let fontFamily = e.currentTarget.getAttribute('data-font')
        let fontName = e.currentTarget.getAttribute('data-name')
        const groupPlacements = window.engravingInstance?.configuratorSetup?.groupPlacements
        const isMonogramProduct = fontName === 'Monogram'
        let code = ''
        if (isMonogramProduct && groupPlacements) {
            setVisibleLine('line1')
            // Products with monogram fonts require special handling.
            if (selectedFace === 'face_1') {
                code = Object.keys(groupPlacements).find(
                    (k) => k.includes('FRONT') && k.includes('MONOGRAM')
                )
            } else if (selectedFace === 'face_2') {
                code = Object.keys(groupPlacements).find(
                    (k) => k.includes('BACK') && k.includes('MONOGRAM')
                )
            }
            setSelectedFaceCode(code)
            await window.engravingController?.commitCustomAttributes(code, {
                'personalization-font': fontFamily
            })
            const siblingCode = getSiblingId(code, groupPlacements)
            if (siblingCode) {
                await window.engravingController?.updatePersonalization(siblingCode, '') // Safe to remove engraving from other fonts. We're doing this just because of the monogram behavior.
            }
            await window.engravingController?.updatePersonalization(
                code,
                engravingLines[selectedFace]?.['monogram']?.length === 3
                    ? engravingLines[selectedFace]?.['monogram']
                    : ''
            )
        }
        setSelectedFont({
            ...selectedFont,
            [selectedFace]: fontName
        })

        if (selectedFaceCode && fontFamily && !isMonogramProduct) {
            if (selectedFace === 'face_1') {
                code = Object.values(groupPlacements).find(
                    (k) => k?.custom?.['ks-face'] === 'face_1' && !k.code?.includes('MONOGRAM')
                )?.code
            } else if (selectedFace === 'face_2') {
                code = Object.keys(groupPlacements).find(
                    (k) => k.includes('BACK') && !k.includes('MONOGRAM')
                )
            }
            await window.engravingController?.commitCustomAttributes(code, {
                'personalization-font': fontFamily
            })
            const siblingCode = getSiblingId(code, groupPlacements)
            if (siblingCode) {
                await window.engravingController?.updatePersonalization(siblingCode, '') // Safe to remove engraving from other fonts. We're doing this just because of the monogram behavior.
            }
            await window.engravingController?.updatePersonalization(
                code,
                `${engravingLines[selectedFace].line1}${
                    engravingLines[selectedFace].line2
                        ? '\n' + engravingLines[selectedFace].line2
                        : ''
                }${
                    engravingLines[selectedFace].line3
                        ? '\n' + engravingLines[selectedFace].line3
                        : ''
                }${
                    engravingLines[selectedFace].line4
                        ? '\n' + engravingLines[selectedFace].line4
                        : ''
                }${
                    engravingLines[selectedFace].line5
                        ? '\n' + engravingLines[selectedFace].line5
                        : ''
                }`
            )
        }
        if (code) {
            // Monogram doesn't offer multi-lines, so we'll disable multilines for it.
            const linesAvailable =
                window.engravingInstance?.configuratorSetup?.groupPlacements?.[code]?.lines
            setShouldShowLineToggle(linesAvailable > 1)
            setShouldShow3LineToggle(linesAvailable > 2)
            setShouldShow4LineToggle(linesAvailable > 3)
            setShouldShow5LineToggle(linesAvailable > 4)
            setSelectedFaceCode(code)

            setEngravingLines((prev) => {
                const updated = {
                    ...prev,
                    [selectedFace]: {
                        ...prev[selectedFace],
                        ['code']: code
                    }
                }
                return updated
            })
        }
        window.setTimeout(() => {
            setShowApiSpinner(false)
        }, 1500)
    }

    useEffect(() => {
        if (openEngraving) {
            onOpen()
        } else {
            onClose()
        }
    }, [openEngraving])

    useEffect(() => {
        setShowInitialSpinner(!!isOpen)
        setTimeout(() => {
            if (document.querySelector('.engraving-drawer-body')) {
                document.querySelector('.engraving-drawer-body').scroll(0, 0)
            }
        }, 300)
    }, [isOpen])

    /**
     * Initialize engraving when the swatch changes.
     */
    useEffect(() => {
        if (
            !product?.c_driveCommerceCustomProductId ||
            (!engravingImages?.face1?.image && !engravingImages?.face2?.image)
        )
            return
        setShowInitialSpinner(true)
        const initializeEngraving = () => {
            const setup = {
                clientRoot: clientRoot,
                blueprintUrl: `${blueprintUrl}${product.c_driveCommerceCustomProductId}/_/_/?apikey=${apiKey}`,
                uploadUrl: uploadUrl,
                recipeUrl: recipeUrl
            }
            const engraving = new EngravingAPI(true)
            engraving.init(setup, product.c_driveCommercePlacementIndependentCode, true)
            window.engravingInstance = engraving

            const interval = setInterval(() => {
                const {configuratorSetup} = window.engravingInstance
                if (configuratorSetup.views) {
                    const isBackOnly =
                        window?.engravingInstance?.configuratorSetup?.views?.face_1?.name === 'Back'
                    Object.values(configuratorSetup.views).forEach((view) => {
                        if (
                            view?.code === 'face_2' &&
                            configuratorSetup.views?.face_1?.name === 'Back'
                        ) {
                            return
                        }
                        if (isBackOnly) {
                            view = window?.engravingInstance?.configuratorSetup?.views?.face_1
                        }
                        const placement = Object.values(configuratorSetup.groupPlacements).find(
                            (k) => k?.custom?.['ks-face'] === view?.code
                        )
                        window.engravingImage?.selectViewByCode(view?.code)
                        const id = Object.keys(fontsMapping)?.find(
                            (k) => fontsMapping?.[k] === selectedFont?.[view?.code]
                        )
                        if (id) {
                            window.engravingController?.commitCustomAttributes(placement.code, {
                                'personalization-font': id
                            })
                        }
                        window.engravingController?.updatePersonalization(
                            placement.code,
                            isMonogram
                                ? engravingLines[view?.code]?.['monogram']?.length === 3
                                    ? engravingLines[view?.code]?.['monogram']
                                    : ''
                                : `${engravingLines[view?.code].line1}${
                                      engravingLines[view?.code].line2
                                          ? '\n' + engravingLines[view?.code].line2
                                          : ''
                                  }${
                                      engravingLines[view?.code].line3
                                          ? '\n' + engravingLines[view?.code].line3
                                          : ''
                                  }${
                                      engravingLines[view?.code].line4
                                          ? '\n' + engravingLines[view?.code].line4
                                          : ''
                                  }${
                                      engravingLines[view?.code].line5
                                          ? '\n' + engravingLines[view?.code].line5
                                          : ''
                                  }`
                        )
                    })

                    /** Set the states for Drive Recipe ID **/
                    saveRecipe().then(async (result) => {
                        setDriveRecipeID(result.id)
                        /** Refresh the gallery images and engraving choice in the purchase path **/
                        const face1Name = isBackOnly
                            ? ''
                            : configuratorSetup?.views?.face_1?.name || ''
                        const face2Name = isBackOnly
                            ? 'Back'
                            : configuratorSetup?.views?.face_2?.name || ''
                        const face1LinesAvailable =
                            selectedFont.face_1 === 'Monogram'
                                ? engravingLines.face_1.monogram
                                : engravingLines.face_1.line1 ||
                                  engravingLines.face_1.line2 ||
                                  engravingLines.face_1.line3 ||
                                  engravingLines.face_1.line4 ||
                                  engravingLines.face_1.line5
                        const activeFace2 = isBackOnly ? 'face_1' : 'face_2'
                        const face2LinesAvailable =
                            selectedFont?.[activeFace2] === 'Monogram'
                                ? engravingLines[activeFace2].monogram
                                : engravingLines[activeFace2].line1 ||
                                  engravingLines[activeFace2].line2 ||
                                  engravingLines[activeFace2].line3 ||
                                  engravingLines[activeFace2].line4 ||
                                  engravingLines[activeFace2].line5
                        const img1 = face1Name
                            ? 'https:' +
                              (isBackOnly
                                  ? window.engravingImage.frames?.[window.engravingImage.frame]?.url
                                  : window.engravingImage.frames?.[1]?.url)
                            : null
                        const img2 = face2Name
                            ? 'https:' +
                              (isBackOnly
                                  ? window.engravingImage.frames?.[window.engravingImage.frame]?.url
                                  : window.engravingImage.frames?.[2]?.url)
                            : null
                        const images = {
                            face1: {
                                image: face1LinesAvailable ? img1 : null,
                                name: face1LinesAvailable ? face1Name : null
                            },
                            face2: {
                                image: face2LinesAvailable ? img2 : null,
                                name: face2LinesAvailable ? face2Name : null
                            }
                        }
                        setEngravingImages(images)
                        setDesktopGalleryEngImages(images)
                        clearInterval(interval)
                        setShowInitialSpinner(false)
                        /** please refresh the checksum **/
                        const activeStates = {
                            face_1: {
                                name: 'face_1-text',
                                hasValue: isBackOnly ? false : !!face1LinesAvailable,
                                value: isBackOnly
                                    ? ''
                                    : selectedFont?.face_1 === 'Monogram'
                                    ? engravingLines?.face_1.monogram
                                    : `${engravingLines.face_1.line1}\n
                                                                    ${engravingLines.face_1.line2}\n
                                                                    ${engravingLines.face_1.line3}\n
                                                                    ${engravingLines.face_1.line4}\n
                                                                    ${engravingLines.face_1.line5}\n`.trim(),
                                fontFamily: selectedFont.face_1
                            },
                            face_1_right: {
                                name: 'face_1-right-text',
                                hasValue: false,
                                value: ''
                            },
                            face_2: {
                                name: 'face_2-text',
                                hasValue: !!face2LinesAvailable,
                                value:
                                    selectedFont?.[isBackOnly ? 'face_1' : 'face_2'] === 'Monogram'
                                        ? engravingLines?.[isBackOnly ? 'face_1' : 'face_2']
                                              ?.monogram
                                        : `${
                                              engravingLines?.[isBackOnly ? 'face_1' : 'face_2']
                                                  .line1
                                          }\n${
                                              engravingLines?.[isBackOnly ? 'face_1' : 'face_2']
                                                  .line2
                                          }\n${
                                              engravingLines?.[isBackOnly ? 'face_1' : 'face_2']
                                                  .line3
                                          }\n${
                                              engravingLines?.[isBackOnly ? 'face_1' : 'face_2']
                                                  .line4
                                          }\n${
                                              engravingLines?.[isBackOnly ? 'face_1' : 'face_2']
                                                  .line5
                                          }`.trim(),
                                fontFamily: selectedFont?.[isBackOnly ? 'face_1' : 'face_2']
                            },
                            face_2_right: {
                                name: 'face_2-right-text',
                                hasValue: false,
                                value: '',
                                fontFamily: ''
                            }
                        }
                        setEngravingChecksum(getHash(activeStates))
                    })
                }
            }, 500)
        }

        requestAnimationFrame(initializeEngraving)
    }, [product?.id])

    /**
     * Initialize engraving when the Drawer opens.
     */
    useEffect(() => {
        if (!product?.c_driveCommerceCustomProductId || !isOpen) return

        const params = new URLSearchParams(window.location.search)
        const paramsReceipeId = params.get('driveRecipeID')
        if (!bodyRef?.current && (!driveRecipeID || (!!driveRecipeID && !!paramsReceipeId))) {
            return
        }
        const initializeEngraving = () => {
            if (!containerRef.current) {
                requestAnimationFrame(initializeEngraving)
                return
            }

            const setup = {
                clientRoot: clientRoot,
                blueprintUrl: `${blueprintUrl}${product.c_driveCommerceCustomProductId}/_/_/?apikey=${apiKey}`,
                uploadUrl: uploadUrl,
                recipeUrl: recipeUrl + (driveRecipeID ? driveRecipeID + '.json' : '')
            }

            if (!driveRecipeID || (!!driveRecipeID && !!paramsReceipeId)) {
                const engraving = new EngravingAPI()
                engraving.init(setup, product.c_driveCommercePlacementIndependentCode)
                window.engravingInstance = engraving
            } else {
                if (document.querySelector('#modal_product__image')) {
                    document.querySelector('#modal_product__image').innerHTML = ''
                }
                const engravingImage = new window.DriveCustomizer.ImageView(
                    window.engravingImage.controller,
                    {
                        container: document.querySelector('#modal_product__image')
                    }
                )
                window.engravingImage = engravingImage
            }

            const interval = setInterval(async () => {
                const {configuratorSetup} = window.engravingInstance
                if (!configuratorSetup?.options || !document.querySelector('.engraving-first-line'))
                    return

                Object.values(configuratorSetup.options).forEach((opt) => {
                    if (opt.definition === 'Engraving Font') {
                        document.querySelectorAll('.engraving-fonts div').forEach((fontBox) => {
                            const label = fontBox.getAttribute('data-name')
                            if (label.toLowerCase() === opt.name.toLowerCase()) {
                                fontBox.classList.remove('hide-element')
                                fontBox.dataset.font = opt.custom['personalization-font'] || ''
                                fontBox.dataset.code = opt.code
                                fontBox.dataset.name = opt.name
                            }
                        })
                    }
                })

                // Enable second line input
                Object.keys(configuratorSetup.options).forEach((key) => {
                    if (key.includes('TEXTHORIZONTAL')) {
                        const option = configuratorSetup.options[key]
                        const secondLineInput = document.querySelector('#engraving-second-line')
                        Object.entries(option.custom).forEach(([k, v]) => {
                            secondLineInput?.setAttribute(`data-${k}`, v)
                        })
                        document
                            .querySelector('.engraving-second-line')
                            ?.classList.remove('hide-element')
                    }
                })

                // Enable face buttons
                let facesAvailable = 0
                Object.values(configuratorSetup.views).forEach((view) => {
                    const btn = document.querySelector(`#${view.code}`)
                    if (!btn) return
                    if (
                        view?.code === 'face_2' &&
                        configuratorSetup.views?.face_1?.name === 'Back'
                    ) {
                        return
                    }
                    if (window?.engravingInstance?.configuratorSetup?.views?.face_1 === 'Back') {
                        view = window?.engravingInstance?.configuratorSetup?.views?.face_1
                    }
                    btn.textContent = view.name
                    btn.classList.remove('hide-element')
                    Object.entries(view).forEach(([key, value]) => {
                        if (key !== 'placements') {
                            btn.dataset[key] = String(value)
                        }
                    })
                    const nonProductPlacement = view.placements.find(
                        (p) =>
                            p.code !== 'Product' &&
                            (selectedFont[view?.code] !== 'Monogram'
                                ? p.code?.toLowerCase()?.indexOf('monogram') === -1
                                : p.code?.toLowerCase()?.indexOf('monogram') !== -1)
                    )
                    if (nonProductPlacement) {
                        btn.dataset.code = nonProductPlacement.code
                    }
                    facesAvailable += 1
                    setShowInitialSpinner(false)

                    const placement = Object.entries(configuratorSetup.groupPlacements).find(
                        ([k]) =>
                            k
                                ?.toLowerCase()
                                ?.indexOf(
                                    view.name?.toLowerCase() === 'top'
                                        ? 'front'
                                        : view.name?.toLowerCase()
                                ) !== -1
                    )?.[1]
                    window.engravingController?.updatePersonalization(
                        engravingLines[view?.code]?.code || placement.code, // If user is reopening the engraving drawer, prioritize the old choice first
                        selectedFont?.[view?.code] === 'Monogram'
                            ? engravingLines[view?.code]?.['monogram']?.length === 3
                                ? engravingLines[view?.code]?.['monogram']
                                : ''
                            : `${engravingLines[view?.code].line1}${
                                  engravingLines[view?.code].line2
                                      ? '\n' + engravingLines[view?.code].line2
                                      : ''
                              }${
                                  engravingLines[view?.code].line3
                                      ? '\n' + engravingLines[view?.code].line3
                                      : ''
                              }${
                                  engravingLines[view?.code].line4
                                      ? '\n' + engravingLines[view?.code].line4
                                      : ''
                              }${
                                  engravingLines[view?.code].line5
                                      ? '\n' + engravingLines[view?.code].line5
                                      : ''
                              }`
                    )
                })
                if (bodyRef?.current) {
                    if (facesAvailable > 1) {
                        bodyRef?.current.classList.remove('single-face')
                    } else {
                        bodyRef?.current.classList.add('single-face')
                    }
                }
                const isBack = configuratorSetup.views?.face_1?.name === 'Back'
                window.engravingImage?.selectViewByCode('face_1')

                const frontPlacement = isBack
                    ? Object.values(configuratorSetup.groupPlacements).find(
                          (p) => p.custom?.['ks-face'] === 'face_1'
                      )
                    : Object.entries(configuratorSetup.groupPlacements).find(([k]) =>
                          k.endsWith('-FRONTTEXT')
                      )?.[1]
                const maxEngravingLength = Object.entries(
                    window.engravingInstance?.configuratorSetup?.options || {}
                ).find(([k]) => k.includes('FONTSIZEMIN'))?.[1]?.custom?.['ks-max-length']
                setMaxLength(parseInt(maxEngravingLength))
                setShouldShowLineToggle(frontPlacement?.lines > 1)
                setSelectedFaceCode(selectedFaceCode || frontPlacement?.code) // If user opens a modal after closing it, make sure to retain the state.
                setShouldShow3LineToggle(
                    window.engravingInstance?.configuratorSetup?.groupPlacements?.[
                        frontPlacement?.code
                    ]?.lines > 2
                )
                setShouldShow4LineToggle(
                    window.engravingInstance?.configuratorSetup?.groupPlacements?.[
                        frontPlacement?.code
                    ]?.lines > 3
                )
                setShouldShow5LineToggle(
                    window.engravingInstance?.configuratorSetup?.groupPlacements?.[
                        frontPlacement?.code
                    ]?.lines > 4
                )
                clearInterval(interval)
                if (selectedFace && document.querySelector(`#${selectedFace}`)) {
                    window.setTimeout(() => {
                        document.querySelector(`#${isBack ? 'face_2' : selectedFace}`).click()
                        /*** The following lines ensures that if the user is returning from the Edit flow, their previously selected fonts are re-applied. ***/
                        document
                            .querySelector(
                                `.engraving-fonts div[data-name="${selectedFont[selectedFace]}"]`
                            )
                            ?.click()
                    }, 700)
                }
            }, 500)
        }

        requestAnimationFrame(initializeEngraving)
    }, [isOpen, bodyRef?.current])

    const isMobile = useBreakpointValue({base: true, md: false})

    const zodiacIcons = getZodiacSigns()

    const selectedZodiacIcons = []

    if (Array.isArray(product?.c_engravingLargeIcons)) {
        for (const iconMap of zodiacIcons) {
            const label = Object.keys(iconMap)[0]
            const value = iconMap[label]

            if (product.c_engravingLargeIcons.includes(label)) {
                selectedZodiacIcons.push(value)
            }
        }
    }

    useEffect(() => {
        let resizeTimeout
        const handleResize = () => {
            clearTimeout(resizeTimeout)
            resizeTimeout = setTimeout(() => {
                if (window.engravingImage) {
                    window.engravingImage.resize()
                }
            }, 700)
        }
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
            clearTimeout(resizeTimeout)
        }
    }, [])

    useEffect(() => {
        if (isOpen) {
            document.querySelector('html').style.overflow = 'hidden'
        } else {
            document.querySelector('html').style.overflow = ''
        }
    }, [isOpen])

    return (
        <Box display="grid">
            <Box display="none">
                <div id="hidden_product__image" />
            </Box>
            <EngravingPurchasePath
                engravingImages={engravingImages}
                highlightTitle={highlightTitle}
                highlightDescription={highlightDescription}
                onOpen={onOpen}
                tagManager={tagManager}
                setEngravingImages={setEngravingImages}
                setDesktopGalleryEngImages={setDesktopGalleryEngImages}
                onClose={onClose}
                setDriveRecipeID={setDriveRecipeID}
                setOpenEngraving={setOpenEngraving}
                navigate={navigate}
            />
            <Drawer
                placement={isMobile ? 'bottom' : 'right'}
                isOpen={isOpen}
                onClose={() => {
                    onClose()
                    setShowInitialSpinner(false)
                    setOpenEngraving(false)
                }}
                size="lg"
                motionPreset={{base: 'slideInBottom', md: 'slideInRight'}}
                scrollBehavior="inside"
                isFullHeight={!isDesktop}
                returnFocusOnClose={false}
            >
                <DrawerOverlay bg="blackAlpha600" />
                <DrawerContent
                    borderRadius={{base: '20px 20px 0 0', md: '0px'}}
                    padding={{base: '0px 20px', md: '0 60px'}}
                    width={{base: '100%', md: '700px'}}
                    maxWidth={{base: '100%', md: '700px'}}
                    height="100%"
                    maxHeight={{base: '95%', md: '100%'}}
                >
                    <DrawerHeader
                        p={{
                            base: '0px !important',
                            md: '30px 0px 20px 0px !important'
                        }}
                        marginTop={{base: '10px', md: '0px'}}
                        display={showInitialSpinner ? 'none' : 'block'}
                        boxShadow="none"
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            borderBottom="0.5px solid"
                            padding="10px 0px"
                            alignItems="center"
                            borderColor="gray.400"
                        >
                            <Text
                                color="black.100"
                                fontFamily="caslon"
                                fontSize="25px"
                                alignContent="center"
                                fontWeight="500"
                                lineHeight="normal"
                                letterSpacing="0.5px"
                            >
                                <FormattedMessage id="engraving" defaultMessage="Engraving" />
                            </Text>
                            <Box
                                padding="0px"
                                borderRadius="50%"
                                onClick={() => {
                                    onClose()
                                    setOpenEngraving(false)
                                }}
                                _hover={{
                                    backgroundColor: {base: 'gray.100', lg: 'transparent'}
                                }}
                                transition="color, 0.3s ease"
                                width="15px"
                                height="15px"
                                cursor="pointer"
                            >
                                <Image
                                    width="15px"
                                    height="15px"
                                    alt="crossed icon"
                                    src={getUrl('UIImages/images/crossed-icon.svg')}
                                />
                            </Box>
                        </Box>
                        <EngravingOptions
                            selectedHeading={selectedHeading}
                            setSelectedHeading={setSelectedHeading}
                            setVisibleLine={setVisibleLine}
                            setDisplayIconType={setDisplayIconType}
                            setShowFonts={setShowFonts}
                            isMonogram={isMonogram}
                            selectedZodiacIcons={selectedZodiacIcons}
                        />
                    </DrawerHeader>
                    {showInitialSpinner && <EngravingSkeleton />}
                    <DrawerBody
                        ref={bodyRef}
                        className="engraving-drawer-body"
                        p={{base: '0px !important', md: '0 0px 0px !important'}}
                        sx={{'&::-webkit-scrollbar': {display: 'none'}}}
                    >
                        {showApiSpinner && (
                            <LoadingSpinner position="absolute" top="auto" displayOverlay="none" />
                        )}
                        <Box
                            sx={{
                                canvas: {
                                    height: '271px !important'
                                }
                            }}
                            marginBottom="20px"
                            id="modal_product__image"
                            ref={containerRef}
                        />

                        <FontOptions
                            showInitialSpinner={showInitialSpinner}
                            selectedFace={selectedFace}
                            handleFaceSelect={handleFaceSelect}
                            setShowFonts={setShowFonts}
                            showFonts={showFonts}
                            setSelectedHeading={setSelectedHeading}
                            selectedFont={selectedFont}
                            handleFontChange={handleFontChange}
                        />

                        {/******* Line 1 Box *******/}
                        <LineOne
                            setVisibleLine={setVisibleLine}
                            visibleLine={visibleLine}
                            showInitialSpinner={showInitialSpinner}
                            shouldShowLineToggle={shouldShowLineToggle}
                            engravingLines={engravingLines}
                            selectedFace={selectedFace}
                            maxLength={maxLength}
                            product={product}
                            setShowFonts={setShowFonts}
                            selectedHeading={selectedHeading}
                            setSelectedHeading={setSelectedHeading}
                            displayIconType={displayIconType}
                            setDisplayIconType={setDisplayIconType}
                            restrictionListArray={restrictionListArray}
                            handleLineChange={handleLineChange}
                            selectedFont={selectedFont}
                        />

                        {/******* Line 2 Box *******/}
                        <LineTwo
                            setVisibleLine={setVisibleLine}
                            visibleLine={visibleLine}
                            showInitialSpinner={showInitialSpinner}
                            shouldShowLineToggle={shouldShowLineToggle}
                            engravingLines={engravingLines}
                            selectedFace={selectedFace}
                            maxLength={maxLength}
                            product={product}
                            setShowFonts={setShowFonts}
                            selectedHeading={selectedHeading}
                            setSelectedHeading={setSelectedHeading}
                            displayIconType={displayIconType}
                            setDisplayIconType={setDisplayIconType}
                            restrictionListArray={restrictionListArray}
                            handleLineChange={handleLineChange}
                            selectedFont={selectedFont}
                        />

                        {/******* Line 3 Box *******/}
                        <LineThree
                            setVisibleLine={setVisibleLine}
                            visibleLine={visibleLine}
                            showInitialSpinner={showInitialSpinner}
                            shouldShow3LineToggle={shouldShow3LineToggle}
                            engravingLines={engravingLines}
                            selectedFace={selectedFace}
                            maxLength={maxLength}
                            product={product}
                            setShowFonts={setShowFonts}
                            selectedHeading={selectedHeading}
                            setSelectedHeading={setSelectedHeading}
                            displayIconType={displayIconType}
                            setDisplayIconType={setDisplayIconType}
                            restrictionListArray={restrictionListArray}
                            handleLineChange={handleLineChange}
                            selectedFont={selectedFont}
                        />

                        {/******* Line 4 Box *******/}
                        <LineFour
                            setVisibleLine={setVisibleLine}
                            visibleLine={visibleLine}
                            showInitialSpinner={showInitialSpinner}
                            shouldShow4LineToggle={shouldShow4LineToggle}
                            engravingLines={engravingLines}
                            selectedFace={selectedFace}
                            maxLength={maxLength}
                            product={product}
                            setShowFonts={setShowFonts}
                            selectedHeading={selectedHeading}
                            setSelectedHeading={setSelectedHeading}
                            displayIconType={displayIconType}
                            setDisplayIconType={setDisplayIconType}
                            restrictionListArray={restrictionListArray}
                            handleLineChange={handleLineChange}
                            selectedFont={selectedFont}
                        />

                        {/******* Line 5 Box *******/}
                        <LineFive
                            setVisibleLine={setVisibleLine}
                            visibleLine={visibleLine}
                            showInitialSpinner={showInitialSpinner}
                            shouldShow5LineToggle={shouldShow5LineToggle}
                            engravingLines={engravingLines}
                            selectedFace={selectedFace}
                            maxLength={maxLength}
                            product={product}
                            setShowFonts={setShowFonts}
                            selectedHeading={selectedHeading}
                            setSelectedHeading={setSelectedHeading}
                            displayIconType={displayIconType}
                            setDisplayIconType={setDisplayIconType}
                            restrictionListArray={restrictionListArray}
                            handleLineChange={handleLineChange}
                            selectedFont={selectedFont}
                        />

                        <EngravingInstructions
                            showInitialSpinner={showInitialSpinner}
                            maxLength={maxLength}
                        />
                        {!showInitialSpinner && (
                            <EngravingButtons
                                showInitialSpinner={showInitialSpinner}
                                isRedtrictedWordActive={isRedtrictedWordActive}
                                engravingLines={engravingLines}
                                restrictionListArray={restrictionListArray}
                                selectedFont={selectedFont}
                                disableAddToBag={disableAddToBag}
                                setShowApiSpinner={setShowApiSpinner}
                                setEngravingImages={setEngravingImages}
                                setDesktopGalleryEngImages={setDesktopGalleryEngImages}
                                saveRecipe={saveRecipe}
                                setDriveRecipeID={setDriveRecipeID}
                                getHash={getHash}
                                makeEngravingGtmObject={makeEngravingGtmObject}
                                addToCart={addToCart}
                                variant={variant}
                                onAddToCartDrawerOpen={onAddToCartDrawerOpen}
                                product={product}
                                priceData={priceData}
                                setOpenBonusModal={setOpenBonusModal}
                                monetate={monetate}
                                customer={customer}
                                wishlist={wishlist}
                                setShowAddedCta={setShowAddedCta}
                                tagManager={tagManager}
                                onClose={onClose}
                                navigate={navigate}
                                setOpenEngraving={setOpenEngraving}
                                setEngravingChecksum={setEngravingChecksum}
                                setEngravingGtmObject={setEngravingGtmObject}
                                isEditEngraving={isEditEngraving}
                                showAddedCta={showAddedCta}
                                productQuantity={productQuantity}
                                updateItemInBasketMutation={updateItemInBasketMutation}
                                basket={basket}
                                productUuid={productUuid}
                            />
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    )
}

Engravable.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.string,
        c_engravingLargeIcons: PropTypes.any,
        master: PropTypes.any,
        c_driveCommercePlacementIndependentCode: PropTypes.any,
        c_driveCommerceCustomProductId: PropTypes.any
    }),
    addToCart: PropTypes.any,
    variant: PropTypes.any,
    driveRecipeID: PropTypes.any,
    wishlist: PropTypes.any,
    priceData: PropTypes.any,
    customer: PropTypes.any,
    disableAddToBag: PropTypes.bool,
    setDriveRecipeID: PropTypes.func,
    openEngraving: PropTypes.bool,
    isEditEngraving: PropTypes.bool,
    setEngravingGtmObject: PropTypes.object,
    basket: PropTypes.any,
    setOpenEngraving: PropTypes.any,
    setDesktopGalleryEngImages: PropTypes.any,
    setEngravingChecksum: PropTypes.func
}

export default Engravable
